import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Box,
  Typography,
  Button,
  Snackbar,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PlaceIcon from "@mui/icons-material/Place";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// Components
import Navbar from "./talent/NavigationTalent";
import TalentSidebar from "./talent/components/TalentSidebar";
import MediaGallery from "./talent/components/MediaGallery";
import VideoReels from "./talent/components/VideoReels";
import AudioReels from "./talent/components/AudioReels";
import ResumeProjectsSection from "./talent/components/ResumeProjectsSection";
import ResumeTextSections from "./talent/components/ResumeTextSections";
import RightNav from "./talent/components/RightNav";
import EmptySkleton from "./talent/components/EmptySkleton.js";
import PageInstructions from "./talent/components/PageInstructions.js";

// Dialogs
import EditProfilePrompt from "./EditProfilePrompt";
import ResumeSectionDialog from "./ResumeSectionDialog";
import MediaEditDialog from "./MediaEditDialog";
import UploadMediaDialog from "./UploadMediaDialog";
import AddResumeProjectSectionDialog from "./talent/components/AddResumeProjectSectionDialog";
import ConfirmDialog from "./talent/components/ConfirmDialog";
import ResumeTextDialog from "./talent/components/ResumeTextDialog";
import ContactDialog from "./talent/components/ContactDialog";
import MeasurementsDialog from "./talent/components/MeasurementsDialog";

// External libraries
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// API
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import { YouTube } from "@mui/icons-material";

const TalentHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Profile and basic data (CORE)
  const [profilePic, setProfilePic] = useState(null);
  const [talentData, setTalentData] = useState({});
  const [affiliations, setAffiliations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vocalTypes, setVocalTypes] = useState([]);
  const [vocalRange, setVocalRange] = useState([]);

  // Media
  const [media, setMedia] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Resume data
  const [resumeSections, setResumeSections] = useState([]);
  const [groupedResumeProjects, setGroupedResumeProjects] = useState([]);

  // UI state
  const [sidebarMediaEditMode, setSidebarMediaEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(true);

  // Edit dialogs
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [editInitialData, setEditInitialData] = useState(null);

  // Resume project dialogs
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [editInitialProject, setEditInitialProject] = useState(null);

  // Delete dialogs
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Text dialog
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [textInitialData, setTextInitialData] = useState({
    resumeTextDataId: 0,
    resumeSectionId: 0,
    resumeTextHeading: "",
    resumeTextDetails: "",
    sortOrder: 0,
    isInternal: 1,
  });

  const containerRef = useRef(null);

  // Media dialogs
  const [mediaEditOpen, setMediaEditOpen] = useState(false);
  const [uploadMediaOpen, setUploadMediaOpen] = useState(false);

  // Contact and representation
  const [contactOpen, setContactOpen] = useState(false);
  const [representationInfo, setRepresentationInfo] = useState({});
  const [headings, setHeadings] = useState([]);

  const userId = Number(localStorage.getItem("userId")) || null;
  const [measurementsOpen, setMeasurementsOpen] = useState(false);

  // Snackbar close
  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // -------- Media processing (safe) --------
  const sortedMedia = useMemo(() => {
    return [...(Array.isArray(media) ? media : [])].sort(
      (a, b) => (a.picSort || 0) - (b.picSort || 0),
    );
  }, [media]);

  const pictureItems = useMemo(
    () => sortedMedia.filter((m) => m.mediaType?.toLowerCase() === "picture"),
    [sortedMedia],
  );

  const videoItems = useMemo(
    () =>
      sortedMedia.filter((m) =>
        ["ytvideo", "vimvideo", "video"].includes(m.mediaType?.toLowerCase()),
      ),
    [sortedMedia],
  );

  const audioItems = useMemo(
    () => sortedMedia.filter((m) => m.mediaType?.toLowerCase() === "audio"),
    [sortedMedia],
  );

  // -------- Core refresh functions --------
  const refreshProfileData = async () => {
    const response = await api.get(ENDPOINTS.getTalentProfile({ userId }));
    const newTalentData = response?.data?.data || {};
    setTalentData(newTalentData);
    return newTalentData;
  };

  const refreshAffiliations = async () => {
    const response = await api.get(ENDPOINTS.getUserAffiliations({ userId }));
    const newAffiliations = response?.data || [];
    setAffiliations(newAffiliations);
    return newAffiliations;
  };

  const refreshVocalTypes = async () => {
    const response = await api.get(
      `/api/TalentProfile/GetUserVocalTypes/${userId}`,
    );
    const vocalTypesData = Array.isArray(response?.data) ? response.data : [];
    const typeNames = vocalTypesData.map((v) => v.vocalTypeName);

    setVocalTypes(typeNames);
    setTalentData((prev) => ({
      ...prev,
      vocalTypeIds: vocalTypesData.map((v) => v.vocalTypeId),
    }));

    return typeNames;
  };

  const refreshVocalRange = async () => {
    const response = await api.get(ENDPOINTS.getVocalRanges());
    const newVocalRange = response?.data?.data || [];
    setVocalRange(newVocalRange);
    return newVocalRange;
  };

  const refreshSidebarData = async () => {
    const res = await api.get(
      ENDPOINTS.getRepresentationInformation({ userId }),
    );
    setRepresentationInfo(res?.data || {});
  };

  // -------- Optional loaders (NO snackbar for empty state) --------
  const fetchResumeSections = async (reason = "unknown") => {
    try {
      const timestamp = Date.now();
      const randomParam = Math.random().toString(36);
      const endpoint = `${ENDPOINTS.getResumeSections({
        userId,
      })}?t=${timestamp}&r=${randomParam}`;

      const response = await api.get(endpoint);
      const data = response?.data?.data || response?.data || [];

      if (!Array.isArray(data)) {
        // Treat as empty; do not show global error for new profiles
        setGroupedResumeProjects([]);
        return;
      }

      setGroupedResumeProjects(data);
    } catch (err) {
      // Optional data -> silent fallback
      console.warn(`[Optional] fetchResumeSections failed (${reason}):`, err);
      setGroupedResumeProjects([]);
    }
  };

  const fetchResumeTextSections = async () => {
    try {
      const response = await api.get(ENDPOINTS.getResumeTextData({ userId }));
      const textData = response?.data || [];

      if (!Array.isArray(textData)) {
        setResumeSections([]);
        return;
      }

      // Keep your existing processing logic
      const processedSections = textData
        .filter((item) => item.activeFlag)
        .map((item) => ({
          ...item,
          resumeTextHeading: (item.resumeTextHeading || "")
            .toLowerCase()
            .split(" ")
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        }))
        .reduce((acc, item) => {
          const existing = acc.find(
            (s) =>
              (s.resumeTextHeading || "").toUpperCase() ===
              (item.resumeTextHeading || "").toUpperCase(),
          );

          if (!existing) {
            acc.push(item);
          } else {
            if (
              new Date(item.dateRecordCreated) >
              new Date(existing.dateRecordCreated)
            ) {
              const index = acc.indexOf(existing);
              acc[index] = item;
            }
          }
          return acc;
        }, []);

      setResumeSections(processedSections);
    } catch (err) {
      console.warn("[Optional] fetchResumeTextSections failed:", err);
      setResumeSections([]);
    }
  };

  const fetchMedia = async ({ showToast = false } = {}) => {
    try {
      const res = await api.get(ENDPOINTS.getUserMedia({ userId }));
      const mediaData = res?.data || [];
      setMedia(Array.isArray(mediaData) ? mediaData : []);

      const profileEntry = (Array.isArray(mediaData) ? mediaData : []).find(
        (m) => m.mediaType?.toLowerCase() === "profile",
      );
      if (profileEntry) setProfilePic(profileEntry.mediaName);

      if (showToast) {
        setSnackbar({
          open: true,
          message: "Media refreshed successfully",
          severity: "success",
        });
      }
    } catch (err) {
      console.warn("[Optional] fetchMedia failed:", err);
      setMedia([]);
      setProfilePic(null);

      if (showToast) {
        setSnackbar({
          open: true,
          message: "Failed to fetch media",
          severity: "error",
        });
      }
    }
  };

  // -------- Event Handlers --------
  const handleSectionAddSuccess = async () => {
    await fetchResumeSections("add section success");
    setTimeout(
      () => fetchResumeSections("add section success - delayed"),
      1000,
    );

    setSnackbar({
      open: true,
      message: "Section added successfully",
      severity: "success",
    });
  };

  const handleResumeSectionSuccess = async () => {
    setResumeDialogOpen(false);
    setEditInitialProject(null);
    window.dispatchEvent(new Event("talent-data-updated"));

    await fetchResumeSections("resume section success");
    setTimeout(
      () => fetchResumeSections("resume section success - delayed"),
      1000,
    );

    setSnackbar({
      open: true,
      message: "Role added successfully",
      severity: "success",
    });
  };

  const handleSaveText = async (section) => {
    try {
      const payload = {
        resumeTextDataId: section.resumeTextDataId || 0, //  USE DIRECTLY
        userId,
        isInternal: section.isInternal || 0,
        resumeTextHeading: section.resumeTextHeading,
        resumeTextDetails: section.resumeTextDetails,
        sortOrder: section.sortOrder || 0,
        userBy: userId,
        activeFlag: true,
      };

      await api.post(ENDPOINTS.addOrUpdateResumeText(), payload);
      await fetchResumeTextSections();

      setIsTextDialogOpen(false);
      setSnackbar({ open: true, message: "Text saved", severity: "success" });
    } catch (err) {
      console.error("handleSaveText error:", err);
      setSnackbar({
        open: true,
        message: "Failed to save text",
        severity: "error",
      });
    }
  };

  const handleDeleteProjectConfirmed = async (project) => {
    if (!project || !project.resumeSectionDetailId) {
      setSnackbar({
        open: true,
        message: "Invalid project selected for deletion",
        severity: "error",
      });
      return;
    }

    try {
      const uid = localStorage.getItem("userId") || "";

      const payload = {
        ...project,
        userId: uid,
        userBy: uid,
        activeFlag: false,
      };

      const resp = await api.post(
        ENDPOINTS.addOrUpdateResumeSectionDetails(),
        payload,
      );

      const result = resp.data;

      if (
        resp.status === 200 &&
        (result.success || result.resumeSectionDetailId)
      ) {
        await fetchResumeSections("delete project");
        setDeleteProjectDialogOpen(false);
        setSnackbar({
          open: true,
          message: "Project deleted successfully",
          severity: "info",
        });
      } else {
        console.error("Delete project failed:", result);
        setSnackbar({
          open: true,
          message: "Failed to delete project",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Error soft-deleting project:", err);
      setSnackbar({
        open: true,
        message: "Delete failed due to a network error",
        severity: "error",
      });
    }
  };

  const handleDeleteSectionConfirmed = async (section) => {
    try {
      await api.post(ENDPOINTS.saveResumeSection(), {
        resumeSectionId: section.resumeSectionId,
        resumeSectionName: section.resumeSectionName || section.heading,
        userId,
        userBy: userId,
        sortOrder: section.sortOrder || 0,
        activeFlag: false,
      });

      await fetchResumeSections("delete section");
      setDeleteSectionDialogOpen(false);
      setSnackbar({ open: true, message: "Section deleted", severity: "info" });
    } catch (err) {
      console.error("Error soft-deleting section", err);
      setSnackbar({
        open: true,
        message: "Failed to delete section",
        severity: "error",
      });
    }
  };

  const handleDeleteResumeText = async (section) => {
    if (!section || !section.resumeTextDataId) return false;

    try {
      await api.post(ENDPOINTS.addOrUpdateResumeText(), {
        ...section,
        activeFlag: false,
        userBy: userId,
      });
      return true;
    } catch (err) {
      console.error("Error deleting resume text section:", err);
      return false;
    }
  };

  const handleDeleteTextConfirmed = async (section) => {
    const ok = await handleDeleteResumeText(section);
    if (ok) {
      await fetchResumeTextSections();
      setSnackbar({ open: true, message: "Text deleted", severity: "info" });
    } else {
      setSnackbar({
        open: true,
        message: "Failed to delete text",
        severity: "error",
      });
    }
    setDeleteDialogOpen(false);
  };

  // -------- updateDocuments (keep your behavior, UX already OK) --------
  const updateDocuments = async (partialUpdate) => {
    try {
      const payload = {
        ...talentData,
        ...partialUpdate,
        userId,
      };

      const res = await api.post(ENDPOINTS.updateTalentProfile(), payload);
      const result = res.data;

      if (res.status !== 200 || !(result.success || result.status === "Ok")) {
        throw new Error("Documents update failed");
      }

      const refreshPromises = [refreshProfileData()];

      if (partialUpdate.vocalTypeIds) refreshPromises.push(refreshVocalTypes());
      if (partialUpdate.vocalRangeID) refreshPromises.push(refreshVocalRange());
      if (partialUpdate.affiliationIds)
        refreshPromises.push(refreshAffiliations());

      await Promise.all(refreshPromises);

      setSnackbar({
        open: true,
        message: "Profile updated successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error updating documents:", err);
      setSnackbar({
        open: true,
        message: "Failed to update profile",
        severity: "error",
      });
    }
  };

  const handleSaveContactAndReps = async ({
    personalContact,
    representationInfo,
  }) => {
    try {
      await api.post(ENDPOINTS.updateTalentProfile(), {
        ...talentData,
        userId,
        email: personalContact.email,
        primaryContactNumber: personalContact.primaryContactNumber,
      });

      await Promise.all(
        Object.entries(representationInfo).map(([headingName, info]) => {
          return api.post(ENDPOINTS.addOrUpdateRepresentation(), {
            userId,
            headingName,
            nameofCompany: info.nameofCompany,
            representativeName: info.representativeName,
            representativeContact: info.representativeContact,
            userBy: userId,
            activeFlag: true,
          });
        }),
      );

      setSnackbar({
        open: true,
        message: "Contact & representation saved!",
        severity: "success",
      });
      setSidebarMediaEditMode(false);
    } catch (err) {
      console.error("Failed saving contact & reps", err);
      setSnackbar({ open: true, message: "Save failed", severity: "error" });
    }
  };

  const handleProfileEditSave = async () => {
    try {
      setEditPromptOpen(false);
      setEditInitialData(null);

      // Trigger FULL refresh
      window.dispatchEvent(new Event("talent-data-updated"));

      if (editSection !== "Name") {
        setSnackbar({
          open: true,
          message: `${editSection} updated successfully`,
          severity: "success",
        });
      }

      setEditSection(null);
    } catch (err) {
      console.error("[SAVE ERROR]", err);
      setSnackbar({
        open: true,
        message: `Failed to update ${editSection}`,
        severity: "error",
      });
    }
  };

  // -------- Edit mode routing logic (keep) --------
  useEffect(() => {
    const isEditMode =
      location.pathname.toLowerCase() === "/editresume" ||
      searchParams.get("mode") === "edit";

    setSidebarMediaEditMode(isEditMode);
    document.title = isEditMode ? "Edit Resume" : "My Resume";
  }, [location.pathname, searchParams]);

  const loadRepresentationInfo = async (userId) => {
    try {
      const headingsRes = await api.get(
        ENDPOINTS.getAllRepresentationHeadings(),
      );

      const headings = headingsRes?.data?.data || headingsRes?.data || [];

      const headingNames = headings.map((h) => h.headingName);

      const requests = headingNames.map((heading) =>
        api.get(ENDPOINTS.getRepresentationInformation(userId, heading)),
      );

      const results = await Promise.allSettled(requests);

      const repData = {};

      results.forEach((res, index) => {
        if (res.status === "fulfilled") {
          repData[headingNames[index]] = res.value?.data || {};
        }
      });

      return {
        headings: headingNames,
        representationInfo: repData,
      };
    } catch (err) {
      console.warn("Representation loading failed", err);
      return { headings: [], representationInfo: {} };
    }
  };
  const loadInitialData = async () => {
    try {
      // CORE PROFILE (must succeed)
      const profileRes = await api.get(ENDPOINTS.getTalentProfile({ userId }));

      const newTalentData = profileRes?.data?.data || {};
      setTalentData(newTalentData);
    } catch (err) {
      console.error("Profile load failed", err);

      setSnackbar({
        open: true,
        message: "Unable to load your profile. Please try again.",
        severity: "error",
      });

      return;
    }

    // OPTIONAL DATA (parallel)
    const optionalCalls = [
      api.get(ENDPOINTS.getUserAffiliations({ userId })),
      api.get(ENDPOINTS.getLocations()),
      api.get(ENDPOINTS.getVocalRanges()),
      api.get(`/api/TalentProfile/GetUserVocalTypes/${userId}`),
      api.get(ENDPOINTS.getUserMedia({ userId })),
      api.get(ENDPOINTS.getResumeSections({ userId })),
      api.get(ENDPOINTS.getResumeTextData({ userId })),
      api.get(ENDPOINTS.getNotifications({ userId, userRole: "Talent" })),
    ];

    const [
      affiliationsRes,
      locationsRes,
      vocalRangeRes,
      vocalTypesRes,
      mediaRes,
      resumeProjectsRes,
      resumeTextRes,
      notificationsRes,
    ] = await Promise.allSettled(optionalCalls);

    // Affiliations
    setAffiliations(affiliationsRes?.value?.data || []);

    // Locations
    setLocations(locationsRes?.value?.data?.data || []);

    // Vocal range
    setVocalRange(vocalRangeRes?.value?.data?.data || []);

    // Vocal types
    const vocalTypesData = Array.isArray(vocalTypesRes?.value?.data)
      ? vocalTypesRes.value.data
      : [];

    setVocalTypes(vocalTypesData.map((v) => v.vocalTypeName));

    setTalentData((prev) => ({
      ...prev,
      vocalTypeIds: vocalTypesData.map((v) => v.vocalTypeId),
    }));

    // Media
    const mediaData = mediaRes?.value?.data || [];

    setMedia(Array.isArray(mediaData) ? mediaData : []);

    const profileEntry = mediaData.find(
      (m) => m.mediaType?.toLowerCase() === "profile",
    );

    setProfilePic(profileEntry?.mediaName || null);

    // Resume projects
    setGroupedResumeProjects(
      resumeProjectsRes?.value?.data?.data ||
        resumeProjectsRes?.value?.data ||
        [],
    );

    // Resume text
    const textData = resumeTextRes?.value?.data || [];

    if (Array.isArray(textData)) {
      const processedText = textData
        .filter((item) => item.activeFlag)
        .map((item) => ({
          ...item,
          resumeTextHeading: (item.resumeTextHeading || "")
            .toLowerCase()
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0].toUpperCase() + w.slice(1))
            .join(" "),
        }));

      setResumeSections(processedText);
    } else {
      setResumeSections([]);
    }

    // Notifications
    const notifData = notificationsRes?.value?.data;

    setNotifications(
      Array.isArray(notifData?.data)
        ? notifData.data.filter((n) => n.activeFlag !== false)
        : [],
    );

    // Representation (separate loader)
    const repData = await loadRepresentationInfo(userId);

    setHeadings(repData.headings);
    setRepresentationInfo(repData.representationInfo);
  };

  // -------- INITIAL LOAD (UX FIX) --------
  useEffect(() => {
    if (!userId) return;
    loadInitialData();
  }, [userId]);

  useEffect(() => {
    if (userId) fetchResumeSections("initial load");
  }, [userId]);

  useEffect(() => {
    if (userId) fetchResumeTextSections();
  }, [userId]);
  useEffect(() => {
    const section = searchParams.get("section");

    if (!section) return;

    const normalizedSection = section.toLowerCase();

    const headingMap = {
      bio: "Bio",
      "special-skills": "Special Skills",
      training: "Training",
    };

    const heading = headingMap[normalizedSection];

    if (!heading) return;

    const existingSection = resumeSections.find(
      (s) => s.resumeTextHeading?.toLowerCase() === heading.toLowerCase(),
    );

    setTextInitialData(
      existingSection || {
        resumeTextDataId: 0,
        resumeSectionId: 0,
        resumeTextHeading: heading,
        resumeTextDetails: "",
        sortOrder: 0,
        isInternal: 1,
      },
    );

    setIsTextDialogOpen(true);

    // remove query param so it won't reopen
    navigate("/editresume", { replace: true });
  }, [searchParams, resumeSections, navigate]);

  useEffect(() => {
    if (!userId) return;

    const refreshAll = async () => {
      try {
        await Promise.all([
          refreshProfileData(),
          fetchMedia(),
          fetchResumeSections("global refresh"),
          fetchResumeTextSections(),
          refreshAffiliations(),
          refreshVocalTypes(),
          refreshVocalRange(),
          refreshSidebarData(),
        ]);
      } catch (err) {
        console.warn("Global refresh failed:", err);
      }
    };

    window.addEventListener("talent-data-updated", refreshAll);

    return () => {
      window.removeEventListener("talent-data-updated", refreshAll);
    };
  }, [userId]);

  /*   const locationLabel =
    (Array.isArray(locations) &&
      locations.find((l) => l.locationCode === talentData?.stateCode)
        ?.locationState) ||
    "Location"; */
  const locationLabel =
    [talentData?.city, talentData?.location].filter(Boolean).join(", ") ||
    "Location";

  const hasAnyMedia =
    pictureItems.length > 0 || videoItems.length > 0 || audioItems.length > 0;

  const hasCredits =
    Array.isArray(groupedResumeProjects) && groupedResumeProjects.length > 0;

  const hasTextSections =
    Array.isArray(resumeSections) && resumeSections.length > 0;

  const existingResumeSectionHeadings = useMemo(() => {
    return Array.from(
      new Set(
        (groupedResumeProjects || [])
          .map((item) => item.sectionHeading || item.resumeSectionName || "")
          .filter(Boolean)
          .map((h) => h.trim().toUpperCase()),
      ),
    );
  }, [groupedResumeProjects]);
  const showRightNav = hasAnyMedia || hasCredits || hasTextSections;
  const LocationActionBar = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" },
        gap: 1.5,
        mb: "14px",
        width: "100%",
      }}
    >
      {/* LEFT - LOCATION */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PlaceIcon sx={{ color: "#e53935", fontSize: 20 }} />
        <Typography sx={{ fontWeight: 600 }}>{locationLabel}</Typography>
      </Box>

      {/* RIGHT - ACTIONS */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          marginLeft: { md: "auto" },
          width: { xs: "100%", md: "auto" },
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => setContactOpen(true)}
          sx={{ cursor: "pointer", fontWeight: 500 }}
        >
          CONTACT
        </Button>

        {sidebarMediaEditMode && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setUploadMediaOpen(true)}
              sx={{ textTransform: "none" }}
            >
              UPLOAD MEDIA
            </Button>

            {hasAnyMedia && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMediaEditOpen(true)}
                sx={{ textTransform: "none" }}
              >
                EDIT MEDIA
              </Button>
            )}
          </>
        )}

        <Button
          variant="contained"
          size="small"
          onClick={() => {
            setSidebarMediaEditMode((prev) => {
              const newState = !prev;
              if (newState) navigate("/editresume");
              else navigate("/myresume");
              return newState;
            });
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {sidebarMediaEditMode ? "DONE EDITING" : "EDIT RESUME"}
        </Button>
      </Box>
    </Box>
  );
  return (
    <>
      <Navbar />

      <Box
        ref={containerRef}
        sx={{
          maxWidth: 1200,
          mx: "auto",
          py: { xs: 1, md: 2 },
          px: { xs: 1, md: 0 },
        }}
      >
        {/* Notifications (only if backend actually sends one) */}
        <Collapse in={open && notifications.length > 0}>
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={async () => {
                  setOpen(false);
                  if (notifications[0]?.id) {
                    api.post(ENDPOINTS.dismissNotification(), {
                      userId: Number(userId),
                      notificationId: notifications[0].id,
                    });
                  }
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>{notifications[0]?.title || "Notification"}</AlertTitle>
            {notifications[0]?.message || "No message available."}
          </Alert>
        </Collapse>

        <PageInstructions storageKey="resumeEditInstructions">
          <strong>Complete Your Resume</strong>
          <br />
          Add your media and experience to build a stronger casting profile.
          <br />
          - Upload *photos, videos, and audio reels
          <br />
          - Add acting credits, training, *special skills, *bio, and
          *measurements
          <br />
          - Check whether you have a passport and/or driver’s license
          <br />
          Note: 1 Photo, Special Skills, Measurements & Bio are required before
          you can submit to a role.
          <br />
          The more complete your resume is, the higher it will appear in casting
          searches — helping casting directors find and consider you faster.
          <br />
          Tip: You can edit your shareable Vanity Link by clicking the pencil
          icon in the header, then share your profile anywhere!
          <br />
        </PageInstructions>
        {/* Main layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {isMobile && <LocationActionBar />}
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: "100%", md: "250px" },
              minWidth: { xs: "100%", md: "250px" },
              flexShrink: 0,
            }}
          >
            <TalentSidebar
              profilePic={profilePic}
              talentData={talentData}
              affiliations={affiliations}
              locations={locations}
              vocalTypes={vocalTypes}
              vocalRange={vocalRange}
              sidebarMediaEditMode={sidebarMediaEditMode}
              setEditPromptOpen={setEditPromptOpen}
              setEditSection={setEditSection}
              setEditInitialData={setEditInitialData}
              representationInfo={representationInfo}
              updateDocuments={updateDocuments}
            />
          </Box>

          {/* Main content */}
          <Box sx={{ flexGrow: 1 }}>
            {!isMobile && <LocationActionBar />}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Media block */}
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "5px",
                  }}
                >
                  {!hasAnyMedia && (
                    <EmptySkleton
                      title="Photos / Video / Audio"
                      editMode={sidebarMediaEditMode}
                      onAdd={() => setUploadMediaOpen(true)}
                      showDivider={false}
                      alwaysVisible
                    />
                  )}

                  {hasAnyMedia && (
                    <>
                      <Box id="pictures" sx={{ scrollMarginTop: "80px" }}>
                        {pictureItems.length > 0 && (
                          <MediaGallery
                            pictures={pictureItems}
                            onImageClick={(index) => {
                              setLightboxIndex(index);
                              setLightboxOpen(true);
                            }}
                          />
                        )}
                      </Box>

                      {videoItems.length > 0 && (
                        <>
                          {pictureItems.length > 0 && (
                            <Divider sx={{ my: 1 }} />
                          )}
                          <Box id="video">
                            <VideoReels videoItems={videoItems} />
                          </Box>
                        </>
                      )}

                      {audioItems.length > 0 && (
                        <>
                          {(pictureItems.length > 0 ||
                            videoItems.length > 0) && (
                            <Divider sx={{ my: 1 }} />
                          )}
                          <Box id="audio">
                            <AudioReels
                              key={audioItems
                                .map((a) => a.mediaId + "-" + a.picSort)
                                .join("_")}
                              audioItems={audioItems}
                            />
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Box>

                {/* Credits */}
                <Box
                  id="credits"
                  sx={{
                    mt: "1px",
                    backgroundColor: "#fff",
                    borderRadius: "5px",
                  }}
                >
                  {!hasCredits && (
                    <EmptySkleton
                      title="Credits"
                      editMode={sidebarMediaEditMode}
                      onAdd={() => setEditSection("AddProjectSection")}
                      showDivider={false}
                      alwaysVisible
                    />
                  )}

                  {hasCredits && (
                    <ResumeProjectsSection
                      groupedResumeProjects={groupedResumeProjects}
                      setResumeDialogOpen={setResumeDialogOpen}
                      setSelectedSectionTitle={setSelectedSectionTitle}
                      setSelectedSectionId={setSelectedSectionId}
                      setEditInitialData={setEditInitialData}
                      setEditSection={setEditSection}
                      setEditPromptOpen={setEditPromptOpen}
                      setSectionToDelete={setSectionToDelete}
                      setDeleteSectionDialogOpen={setDeleteSectionDialogOpen}
                      setProjectToDelete={setProjectToDelete}
                      setDeleteProjectDialogOpen={setDeleteProjectDialogOpen}
                      editMode={sidebarMediaEditMode}
                    />
                  )}
                </Box>

                {/* Text sections */}
                <Box id="textSections" sx={{ mt: "1px" }}>
                  <ResumeTextSections
                    resumeSections={resumeSections}
                    resumeEditMode={sidebarMediaEditMode}
                    setEditInitialData={setTextInitialData}
                    setSectionToDelete={setSectionToDelete}
                    setDeleteDialogOpen={setDeleteDialogOpen}
                    setEditPromptOpen={setIsTextDialogOpen}
                    showAddNew={true}
                    defaultSections={[
                      "Special Skills",
                      "Training",
                      "Bio",
                      "Custom Section",
                    ]} // ADD THIS
                  />
                </Box>
              </Box>

              {showRightNav && !isMobile && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 50,
                    alignSelf: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <RightNav />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dialogs */}
      <UploadMediaDialog
        open={uploadMediaOpen}
        onClose={() => setUploadMediaOpen(false)}
        userId={userId}
        onUploadSuccess={() => {
          window.dispatchEvent(new Event("talent-data-updated"));
        }}
      />

      <MediaEditDialog
        open={mediaEditOpen}
        onClose={() => setMediaEditOpen(false)}
        userId={userId}
        onMediaUpdate={() => {
          window.dispatchEvent(new Event("talent-data-updated"));
        }}
      />

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={pictureItems.map((item) => ({ src: item.mediaName }))}
      />

      <ResumeSectionDialog
        open={resumeDialogOpen}
        onClose={() => {
          setResumeDialogOpen(false);
          setEditInitialProject(null);
        }}
        selectedSectionTitle={selectedSectionTitle}
        selectedSectionId={selectedSectionId}
        editProject={editInitialProject}
        refresh={() => fetchResumeSections("dialog refresh")}
        onSuccess={handleResumeSectionSuccess}
        userId={userId}
      />

      <AddResumeProjectSectionDialog
        open={editSection === "AddProjectSection"}
        onClose={() => setEditSection(null)}
        onSuccess={handleSectionAddSuccess}
        userId={userId}
        existingHeadings={existingResumeSectionHeadings}
      />

      <EditProfilePrompt
        open={editPromptOpen}
        onClose={() => {
          setEditPromptOpen(false);
          setEditSection(null);
          setEditInitialData(null);
        }}
        initialData={editInitialData}
        section={editSection}
        talentData={talentData}
        locations={locations}
        vocalRange={vocalRange}
        onSave={handleProfileEditSave}
      />

      <ResumeTextDialog
        open={isTextDialogOpen}
        onClose={() => setIsTextDialogOpen(false)}
        initialData={textInitialData}
        existingSections={resumeSections}
        onSave={handleSaveText}
      />

      <ConfirmDialog
        open={deleteProjectDialogOpen}
        onClose={() => setDeleteProjectDialogOpen(false)}
        onConfirm={() => handleDeleteProjectConfirmed(projectToDelete)}
        title="Delete Project"
        message="Are you sure you want to delete this resume credit?"
      />

      <ConfirmDialog
        open={deleteSectionDialogOpen}
        onClose={() => setDeleteSectionDialogOpen(false)}
        onConfirm={() => handleDeleteSectionConfirmed(sectionToDelete)}
        title="Delete Section"
        message="Deleting this section will remove all credits inside it. Continue?"
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleDeleteTextConfirmed(sectionToDelete)}
        title="Delete Text Section"
        message="Are you sure you want to delete this text section?"
      />
      {contactOpen && (
        <ContactDialog
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          contact={{
            phone: talentData.primaryContactNumber || "",
            secondaryPhone: talentData.secondaryContactNumber || "",
            email: talentData.email || "",
            faceBook: talentData.faceBook || "",
            twitter: talentData.twitter || "",
            instagram: talentData.instagram || "",
            tikTok: talentData.tikTok || "",
            personalWebSite: talentData.personalWebSite || "",
            paymentLink1: talentData.paymentLink1 || "",
            paymentLink2: talentData.paymentLink2 || "",
            paymentLink3: talentData.paymentLink3 || "",
            paymentLabel1: talentData.paymentLabel1 || "",
            paymentLabel2: talentData.paymentLabel2 || "",
            paymentLabel3: talentData.paymentLabel3 || "",
            imdb: talentData.imdb || "",
            youTube: talentData.youTube || "",
            snapChat: talentData.snapChat || "",
          }}
          headings={headings || []}
          representationInfoList={representationInfo || {}}
          editMode={sidebarMediaEditMode}
          showMessageButton={false}
          onSave={handleSaveContactAndReps}
        />
      )}

      {/*  Snackbar now respects severity */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <MeasurementsDialog
        open={measurementsOpen}
        onClose={() => {
          setMeasurementsOpen(false);
          navigate("/editresume", { replace: true });
        }}
        talentData={talentData}
        onSave={() => {
          window.dispatchEvent(new Event("talent-data-updated"));
          setMeasurementsOpen(false);
          navigate("/editresume", { replace: true });
        }}
      />
    </>
  );
};

export default TalentHome;
